/**
 * ======================================================================
 * MASTER FRAMEWORK - the live-worklist source registry.
 *
 * A worklist tile binds to LIVE data by an OPEN `source` string (see
 * TileDef.live). This module is the SINGLE place that knows how to turn a
 * source key into rows: one registry (`LIVE_SOURCES`) maps a stable natural key
 * to a lightweight descriptor - which columns to render, which query hook feeds
 * it, where a row deep-links, and how it exports. The generic `LiveTable`
 * component (see live-table.tsx) is a pure shell around that descriptor.
 *
 * This is the "one resolver" model: adding a new worklist source is exactly ONE
 * new entry here - never a new component, never a new branch in the dashboard
 * page, never an enum edit. It mirrors the backend dataset resolver registry
 * (apps/dashboards/resolvers.py) on the client side.
 *
 * CLIENT-AGNOSTIC: nothing here references a tenant. The columns a tile shows,
 * its filters, and its sort come from the tile's `live` binding (config); this
 * module only knows HOW to render each built-in column and HOW to fetch each
 * source. The claims source additionally exposes a generic column CATALOG so a
 * tile can pick which columns (built-in or any EAV attribute) it wants.
 * ======================================================================
 */

import { useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'

import {
  type ActiveFilter,
  type DataTableColumn,
} from '@/admin/components/data-table'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { useClaims, type ClaimRow } from '@/admin/hooks/use-claims'
import { Badge } from '@/shared/components/ui/badge'
import { useHoldings } from '@/admin/hooks/use-products'
import { useTransfers, type Transfer } from '@/admin/hooks/use-transfers'
import { type InspectionRow } from '@/admin/hooks/use-inspections'
import { useWarranties, type WarrantyListRow } from '@/admin/hooks/use-warranties'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { FLAGS, type FeatureFlagKey } from '@/admin/hooks/use-feature-flag'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import { useRegistrationTableColumns } from '@/admin/pages/registrations/use-registration-table-columns'
import type { DataSetRef } from './dashboard-types'
import { plannedWarranty } from '@/admin/lib/warranty-record'

/** A row as it crosses the generic boundary; each source casts to its own type. */
export type LiveRow = Record<string, unknown>

/** Everything the generic table hands a source's data hook. */
export interface LiveSourceContext {
  binding: DataSetRef
  page: number
  limit: number
  search: string
  activeFilters: ActiveFilter[]
  filterValues: Record<string, string[]>
  /** Built columns (for export) + the user's current visibility. */
  columns: DataTableColumn<LiveRow>[]
  columnVisibility: Record<string, boolean>
}

/** The normalised envelope every source's data hook returns. */
export interface LiveDataResult {
  rows: LiveRow[]
  totalCount?: number
  filterFacets: ProductFilterFacet[]
  isPending: boolean
  isFetching: boolean
  isError: boolean
  refetch: () => void
  exportCsv: () => void
  isExporting: boolean
}

/** One registered worklist source. */
export interface LiveSource {
  /** Human label (used in the "not enabled" fallback). Tenant-agnostic. */
  label: string
  searchPlaceholder: string
  /** Optional capability gate; when set, the tile only renders where enabled. */
  requiresFlag?: FeatureFlagKey
  /** Whether the source paginates server-side (drives the pager). */
  paginated: boolean
  buildColumns: (
    formatDate: (iso: string) => string,
    binding: DataSetRef,
  ) => DataTableColumn<LiveRow>[]
  /**
   * Optional hook that supplies columns from a shared source of truth
   * instead of `buildColumns`. When present, `LiveTable` prefers this over
   * `buildColumns` so the tile renders the exact same column definitions as
   * the source's own list page (e.g. registrations). A React hook - the
   * generic table keeps `source` stable so calling it unconditionally here
   * is safe.
   */
  useColumns?: (binding: DataSetRef) => DataTableColumn<LiveRow>[] | undefined
  /** A React hook - the generic table keeps `source` stable so this is safe. */
  useData: (ctx: LiveSourceContext) => LiveDataResult
  rowHref: (row: LiveRow) => string | undefined
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function prettify(v: unknown): string {
  if (v == null || v === '') return '-'
  return String(v)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * A breakdown row's records: the segment plus the card's date window. Every
 * list API reads these the same way (apps.dashboards.segments), and the
 * segment dates its records by the card's own rule (e.g. "Closed over time"
 * by closing date), so the window is not sent as the list's created_after.
 */
export function segmentParams(binding: DataSetRef): Record<string, string> {
  if (!binding.segment) return {}
  const p: Record<string, string> = { dashboard_segment: binding.segment }
  if (binding.createdAfter) p.segment_after = binding.createdAfter
  return p
}

/**
 * Build the standard list query params for a server-paginated source: the
 * tile's slice (`extra`) + the user's search + active filters + saved filters.
 */
function pagedParams(ctx: LiveSourceContext, extra: Record<string, string>): Record<string, string> {
  const p: Record<string, string> = {
    page: String(ctx.page),
    page_size: String(ctx.limit),
    ...extra,
    ...segmentParams(ctx.binding),
    // The card's own filters (e.g. a "By category" card's product template).
    ...(ctx.binding.params ?? {}),
  }
  if (ctx.search.trim()) p.search = ctx.search.trim()
  ctx.activeFilters.forEach((f) => {
    p[f.field] = f.value
  })
  for (const [key, values] of Object.entries(ctx.filterValues)) {
    if (Array.isArray(values) && values.length) p[key] = values.join(',')
  }
  return p
}

/** CSV of the currently displayed rows using each column's text getter. */
function clientCsv(
  tableId: string,
  columns: DataTableColumn<LiveRow>[],
  rows: LiveRow[],
  getText: (row: LiveRow, col: DataTableColumn<LiveRow>) => string,
): void {
  const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`
  const header = columns.map((c) => esc(String(c.header ?? c.id))).join(',')
  const body = rows.map((r) => columns.map((c) => esc(getText(r, c))).join(',')).join('\n')
  const csv = `${header}\n${body}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${tableId}.csv`
  a.click()
  // Defer revocation so the browser has processed the click and started the
  // download before the object URL is released (revoking synchronously can
  // abort the download in some browsers).
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

// ---------------------------------------------------------------------------
// Claims - the richest source: a generic column CATALOG the tile picks from.
// ---------------------------------------------------------------------------

function claimStateBadge(row: ClaimRow): ReactNode {
  return <StatusBadge status={row.claim_state || '-'} color={row.claim_state_color ?? undefined} />
}

/** Whole weekdays (Mon-Fri) elapsed since `from`. */
function businessDaysSince(from: number): number {
  const day = 86_400_000
  let n = 0
  for (let t = from + day; t <= Date.now(); t += day) {
    const wd = new Date(t).getDay()
    if (wd !== 0 && wd !== 6) n += 1
  }
  return n
}

function claimTimeInStatus(row: ClaimRow): { label: string; over: boolean } {
  const iso = row.status_entered_at || row.updated_at
  if (!iso) return { label: '-', over: false }
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return { label: '-', over: false }
  const ms = Date.now() - t
  const h = Math.max(0, Math.floor(ms / 3_600_000))
  const d = Math.floor(h / 24)
  // Past due for follow-up: more than 2 business days in the same status.
  return { label: d > 0 ? `${d}d ${h % 24}h` : `${h}h`, over: businessDaysSince(t) > 2 }
}

function claimSortValue(row: ClaimRow, id: string): number | null {
  const iso =
    id === 'time_in_status'
      ? row.status_entered_at || row.updated_at
      : id === 'open_date'
        ? row.created_at
        : id === 'last_updated'
          ? row.updated_at
          : id === 'closed_date'
            ? row.closed_at || null
            : null
  if (!iso) return null
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? null : t
}

const CLAIMS_DEFAULT_COLUMN_IDS = [
  'claim_number',
  'claim_type',
  'customer',
  'product',
  'current_status',
  'rs_assigned',
  'open_date',
]

function buildClaimColumn(
  spec: NonNullable<DataSetRef['columns']>[number],
  fmt: (iso?: string | null) => string,
): DataTableColumn<ClaimRow> | null {
  // Generic attribute column: read any EAV value by code_name.
  if (spec.code_name) {
    const code = spec.code_name
    return {
      id: `attr_${code}`,
      header: spec.label ?? prettify(code),
      cell: (r) => {
        const raw = (r.attribute_values ?? {})[code]
        const text = prettify(raw)
        if (spec.display === 'chip' && text !== '-') {
          return (
            <span className="inline-block whitespace-nowrap rounded-full border-[0.5px] border-[var(--rule)] px-[9px] py-[2px] text-[11px] text-[var(--ink-2)]">
              {text}
            </span>
          )
        }
        return text
      },
    }
  }
  const label = spec.label
  switch (spec.id) {
    case 'open_date':
      return { id: 'open_date', header: label ?? 'Created', sortable: true, cell: (r) => fmt(r.created_at) }
    case 'closed_date':
      return { id: 'closed_date', header: label ?? 'Closed date', sortable: true, cell: (r) => fmt(r.closed_at) }
    case 'claim_number':
      return {
        id: 'claim_number',
        header: label ?? 'Claim #',
        cell: (r) => (
          <span className="font-medium text-[var(--green)]">
            {r.claim_number || `CLM-${r.uuid.slice(0, 8).toUpperCase()}`}
          </span>
        ),
      }
    case 'product':
      return { id: 'product', header: label ?? 'Product', cell: (r) => r.product_name || '-' }
    case 'current_status':
      return {
        id: 'current_status',
        header: label ?? 'State',
        cell: claimStateBadge,
        meta: { filterType: 'text', filterField: 'claim_state' },
      }
    case 'time_in_status':
      return {
        id: 'time_in_status',
        header: label ?? 'Time in status',
        sortable: true,
        cell: (r) => {
          const t = claimTimeInStatus(r)
          return (
            <span className={t.over ? 'font-semibold text-[var(--status-error)]' : undefined}>{t.label}</span>
          )
        },
      }
    case 'last_updated':
      // Sortable: latest / oldest activity first.
      return { id: 'last_updated', header: label ?? 'Last updated', sortable: true, cell: (r) => fmt(r.updated_at) }
    case 'claim_type':
      return {
        id: 'claim_type',
        header: label ?? 'Type',
        cell: (r) => r.claim_schema_name || '-',
        meta: { filterType: 'text', filterField: 'claim_type' },
      }
    case 'product_type':
      return { id: 'product_type', header: label ?? 'Product', cell: (r) => r.product_type || '-' }
    case 'action_reasons':
      return {
        id: 'action_reasons',
        header: label ?? 'Why',
        cell: (r) =>
          r.action_reasons?.length ? (
            <span className="flex flex-wrap gap-1">
              {r.action_reasons.map((reason) => (
                <Badge
                  key={reason.key}
                  variant="outline"
                  // Someone waiting on the user (the API says which) stands out.
                  className={
                    reason.tone === 'urgent'
                      ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                      : 'text-[var(--ink-2)]'
                  }
                >
                  {reason.label}
                </Badge>
              ))}
            </span>
          ) : (
            ''
          ),
      }
    case 'product_line':
      return { id: 'product_line', header: label ?? 'Product line', cell: (r) => r.product_line || '-' }
    case 'estimated_amount':
      return {
        id: 'estimated_amount',
        header: label ?? 'Estimated amount',
        cell: (r) =>
          r.estimated_amount == null || r.estimated_amount === ''
            ? '-'
            : Number(r.estimated_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      }
    case 'contractor':
      // Blank (not a dash) when no contractor is assigned.
      return { id: 'contractor', header: label ?? 'Contractor', cell: (r) => r.contractor_name || '' }
    case 'rs_assigned':
      return {
        id: 'rs_assigned',
        header: label ?? 'Assignee',
        cell: (r) => r.assigned_to?.name || '-',
        meta: { filterType: 'text', filterField: 'assigned_to' },
      }
    case 'current_assignee':
      return {
        id: 'current_assignee',
        header: label ?? 'Current assignee',
        cell: (r) => r.assigned_to?.name || '-',
        meta: { filterType: 'text', filterField: 'assigned_to' },
      }
    case 'customer':
      return { id: 'customer', header: label ?? 'Customer', cell: (r) => r.consumer_account_name || '-' }
    case 'payment_type':
      return {
        id: 'payment_type',
        header: label ?? 'Payment type',
        cell: (r) => (r.payment_type ? prettify(r.payment_type) : '-'),
      }
    case 'previous_status':
      return { id: 'previous_status', header: label ?? 'Previous status', cell: (r) => r.previous_status || '-' }
    default:
      return null
  }
}

const claimsSource: LiveSource = {
  label: 'Claims',
  searchPlaceholder: 'Search claims...',
  paginated: true,
  requiresFlag: FLAGS.CLAIMS,
  buildColumns: (formatDate, binding) => {
    const fmt = (iso?: string | null) => (iso ? formatDate(iso) : '-')
    const specs =
      binding.columns && binding.columns.length
        ? binding.columns
        : CLAIMS_DEFAULT_COLUMN_IDS.map((id) => ({ id }))
    const cols = specs
      .map((s) => buildClaimColumn(s, fmt))
      .filter((c): c is DataTableColumn<ClaimRow> => c !== null)
    return cols as unknown as DataTableColumn<LiveRow>[]
  },
  useData: (ctx) => {
    const { binding } = ctx
    const extra: Record<string, string> = {}
    if (binding.state) extra.claim_state = binding.state
    if (binding.assignedToMe) extra.assigned_to = 'me'
    if (binding.watchedByMe) extra.watched_by_me = 'true'
    if (binding.closedWithinDays) extra.active_or_closed_within_days = String(binding.closedWithinDays)
    if (binding.withEstimatedAmount) extra.with_estimated_amount = 'true'
    if (binding.needsMyAction) extra.needs_my_action = 'true'
    const params = useMemo(
      () => pagedParams(ctx, extra),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ctx.page, ctx.limit, ctx.search, ctx.activeFilters, ctx.filterValues, binding],
    )
    const { data, isPending, isFetching, isError, refetch } = useClaims(params, { keepPreviousPage: true })
    const defaultSort = binding.defaultSort
    const rows = useMemo(() => {
      const base = (data?.results ?? []) as ClaimRow[]
      const sorted = defaultSort
        ? [...base].sort((a, b) => {
            const dir = defaultSort.dir === 'desc' ? -1 : 1
            const av = claimSortValue(a, defaultSort.id)
            const bv = claimSortValue(b, defaultSort.id)
            if (av == null && bv == null) return 0
            if (av == null) return 1
            if (bv == null) return -1
            return (av - bv) * dir
          })
        : base
      return sorted as unknown as LiveRow[]
    }, [data?.results, defaultSort])
    const { exportCsv, isExporting } = useTableExport<LiveRow>({
      endpoint: '/claims/claims/',
      columns: ctx.columns,
      columnVisibility: ctx.columnVisibility,
      params,
    })
    return {
      rows,
      totalCount: data?.count,
      filterFacets: data?.filter_options ?? [],
      isPending,
      isFetching,
      isError,
      refetch,
      exportCsv,
      isExporting,
    }
  },
  rowHref: (row) => `/claims/${(row as unknown as ClaimRow).uuid}`,
}

// ---------------------------------------------------------------------------
// Registrations
// ---------------------------------------------------------------------------

interface RegistrationRow {
  uuid: string
  registration_number?: string
  status?: string
  created_at?: string
  dealer_name?: string
  product?: { cached_short_name?: string; cached_long_name?: string } | null
  consumer_account?: { first_name?: string; last_name?: string } | null
  partnership?: {
    downstream_organisation_name?: string
    membership_number?: string
    /** The partnership fields the tenant shows in lists, by attribute code. */
    attributes?: Record<string, unknown>
  } | null
  host?: {
    address?: {
      display?: string
      value?: { address_line_1?: string; address_line_2?: string; locality?: string; region?: string }
    } | null
  } | null
  warranties?: Array<{ class_name?: string; payment_status?: string | null; payment_date?: string | null }>
  /** The warranty requested but not yet issued (e.g. held by an exception). */
  pending_warranty_summary?: { class_name?: string } | null
  coverage?: { state: string } | null
  /** Where the registration stands in each of its workflows (e.g. payment). */
  workflow_states?: Array<{
    workflow_key?: string
    state_key?: string
    state_label?: string
    is_terminal?: boolean
    completed_at?: string | null
    /** What the workflow is for, as the tenant declared it ("payment"). */
    purpose?: string | null
  }>
}

/**
 * The registration's current state in the given workflow, else in its
 * payment workflow (the one the tenant declared is for payment).
 */
function workflowState(r: RegistrationRow, key?: string) {
  const states = r.workflow_states ?? []
  return (
    (key ? states.find((s) => s.workflow_key === key) : undefined) ??
    states.find((s) => s.purpose === 'payment')
  )
}

/**
 * The warranty that carries a registration's payment: the first one that is
 * actually paid for or owed (not "no charge" / "waived"), else the first.
 */
function payableWarranty(r: RegistrationRow) {
  const ws = r.warranties ?? []
  return ws.find((w) => w.payment_status === 'paid' || w.payment_status === 'unpaid') ?? ws[0]
}

/** One registration column from a card's column spec (WPS-232 #9). */
function buildRegistrationColumn(
  spec: NonNullable<DataSetRef['columns']>[number],
  fmt: (iso?: string | null) => string,
): DataTableColumn<RegistrationRow> | null {
  const label = spec.label
  // A value from the contractor's partnership record, by attribute code.
  if (spec.id === 'partnership_attr' && spec.code_name) {
    const code = spec.code_name
    return {
      id: `partnership_attr_${code}`,
      header: label ?? prettify(code),
      cell: (r) => prettify(r.partnership?.attributes?.[code]),
    }
  }
  switch (spec.id) {
    case 'registration_number':
      return {
        id: 'registration_number',
        header: label ?? 'Registration #',
        cell: (r) => (
          <span className="font-medium text-[var(--green)]">
            {r.registration_number || `REG-${r.uuid.slice(0, 8).toUpperCase()}`}
          </span>
        ),
      }
    case 'registration_type':
      return {
        id: 'registration_type',
        header: label ?? 'Registration type',
        // The issued warranty's class, else the one requested (a registration
        // held in the exception queue often has no issued warranty yet).
        cell: (r) => r.warranties?.[0]?.class_name || plannedWarranty(r)?.class_name || '-',
      }
    case 'contractor':
      return {
        id: 'contractor',
        header: label ?? 'Contractor',
        cell: (r) => r.dealer_name || r.partnership?.downstream_organisation_name || '-',
      }
    case 'registration_date':
      return { id: 'registration_date', header: label ?? 'Registration date', sortable: true, cell: (r) => fmt(r.created_at) }
    case 'payment_status':
      return {
        id: 'payment_status',
        header: label ?? 'Payment status',
        // The configured payment workflow's state (e.g. "Unpaid"), which every
        // registration has - else the issued warranty's payment status.
        cell: (r) => workflowState(r, spec.workflow_key)?.state_label || prettify(payableWarranty(r)?.payment_status),
      }
    case 'paid_date':
      return {
        id: 'paid_date',
        header: label ?? 'Paid date',
        // Only once paid; blank otherwise. With a payment workflow configured:
        // the day it reached its paid state (its terminal "Paid" step) - a
        // "No charge" end is not a payment, so it has no paid date.
        cell: (r) => {
          const wf = workflowState(r, spec.workflow_key)
          if (wf) return wf.is_terminal && wf.state_key === 'paid' && wf.completed_at ? fmt(wf.completed_at) : ''
          const w = payableWarranty(r)
          return w?.payment_status === 'paid' && w.payment_date ? fmt(w.payment_date) : ''
        },
      }
    case 'owner':
      return { id: 'owner', header: label ?? 'Owner', cell: (r) => registrationOwner(r) }
    case 'address': {
      return {
        id: 'address',
        header: label ?? 'Address',
        cell: (r) => {
          const v = r.host?.address?.value
          return [v?.address_line_1, v?.address_line_2].filter(Boolean).join(', ') || '-'
        },
      }
    }
    case 'city':
      return { id: 'city', header: label ?? 'City', cell: (r) => r.host?.address?.value?.locality || '-' }
    case 'state':
      return { id: 'state', header: label ?? 'State', cell: (r) => r.host?.address?.value?.region || '-' }
    case 'status':
      return { id: 'status', header: label ?? 'Status', cell: (r) => r.status || '-' }
    default:
      return null
  }
}

function registrationOwner(r: RegistrationRow): string {
  return [r.consumer_account?.first_name, r.consumer_account?.last_name].filter(Boolean).join(' ') || '-'
}

const registrationsSource: LiveSource = {
  label: 'Registrations',
  searchPlaceholder: 'Search registrations...',
  paginated: true,
  requiresFlag: FLAGS.REGISTRATIONS,
  buildColumns: (formatDate, binding) => {
    // A card that names its columns (e.g. a Raw data table) gets exactly those.
    if (binding?.columns?.length) {
      const fmt = (iso?: string | null) => (iso ? formatDate(iso) : '-')
      return binding.columns
        .map((spec) => buildRegistrationColumn(spec, fmt))
        .filter((c): c is DataTableColumn<RegistrationRow> => c !== null) as unknown as DataTableColumn<LiveRow>[]
    }
    const cols: DataTableColumn<RegistrationRow>[] = [
      {
        id: 'registration_number',
        header: 'Registration #',
        cell: (r) => (
          <span className="font-medium text-[var(--green)]">
            {r.registration_number || `REG-${r.uuid.slice(0, 8).toUpperCase()}`}
          </span>
        ),
      },
      { id: 'owner', header: 'Owner', cell: (r) => registrationOwner(r) },
      {
        id: 'product',
        header: 'Product',
        cell: (r) => r.product?.cached_short_name || r.product?.cached_long_name || '-',
      },
      { id: 'warranty_class', header: 'Warranty category', cell: (r) => r.warranties?.[0]?.class_name || '-' },
      {
        id: 'contractor',
        header: 'Contractor',
        cell: (r) => r.dealer_name || r.partnership?.downstream_organisation_name || '-',
      },
      { id: 'status', header: 'Status', cell: (r) => r.status || '-' },
      {
        id: 'created',
        header: 'Registered',
        sortable: true,
        cell: (r) => (r.created_at ? formatDate(r.created_at) : '-'),
      },
    ]
    return cols as unknown as DataTableColumn<LiveRow>[]
  },
  // Single source of truth: reuse the exact column pool the Registrations
  // list page resolves (config-driven columns + template/attribute columns),
  // filtered to the default-visible set so the tile shows the same columns
  // an admin sees by default on the full list page.
  useColumns: (binding) => {
    const baseColumns = useRegistrationTableColumns()
    // A card that names its own columns uses buildColumns instead.
    if (binding.columns?.length) return undefined
    return baseColumns.filter((c) => c.defaultVisible !== false) as unknown as DataTableColumn<LiveRow>[]
  },
  useData: (ctx) => {
    const { binding } = ctx
    const extra: Record<string, string> = {}
    if (binding.status) extra.status = binding.status
    const params = useMemo(
      () => pagedParams(ctx, extra),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ctx.page, ctx.limit, ctx.search, ctx.activeFilters, ctx.filterValues, binding],
    )
    const { data, isPending, isFetching, isError, refetch } = useHoldings(params, { keepPreviousPage: true })
    const { exportCsv, isExporting } = useTableExport<LiveRow>({
      endpoint: '/registrations/',
      columns: ctx.columns,
      columnVisibility: ctx.columnVisibility,
      params,
    })
    return {
      rows: (data?.results ?? []) as unknown as LiveRow[],
      totalCount: data?.count,
      filterFacets: data?.filter_options ?? [],
      isPending,
      isFetching,
      isError,
      refetch,
      exportCsv,
      isExporting,
    }
  },
  rowHref: (row) => `/registrations/${(row as { uuid: string }).uuid}`,
}

// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------

const transfersSource: LiveSource = {
  label: 'Transfers',
  searchPlaceholder: 'Search transfers...',
  paginated: true,
  requiresFlag: FLAGS.TRANSFERS,
  buildColumns: (formatDate) => {
    const cols: DataTableColumn<Transfer>[] = [
      {
        id: 'new_owner',
        header: 'New owner',
        cell: (r) => `${r.new_owner_first_name} ${r.new_owner_last_name}`.trim() || '-',
      },
      { id: 'property', header: 'Property', cell: (r) => r.host_address?.display || '-' },
      { id: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status_label ?? r.status} /> },
      { id: 'transfer_type', header: 'Transfer type', cell: (r) => r.transfer_type?.label ?? '-' },
      {
        id: 'ownership_change_date',
        header: 'Ownership change',
        cell: (r) => (r.ownership_change_date ? formatDate(r.ownership_change_date) : '-'),
      },
      {
        id: 'created_at',
        header: 'Created',
        sortable: true,
        cell: (r) => (r.created_at ? formatDate(r.created_at) : '-'),
      },
    ]
    return cols as unknown as DataTableColumn<LiveRow>[]
  },
  useData: (ctx) => {
    const { binding } = ctx
    const extra: Record<string, string> = {}
    if (binding.status) extra.status = binding.status
    const params = useMemo(
      () => pagedParams(ctx, extra),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ctx.page, ctx.limit, ctx.search, ctx.activeFilters, ctx.filterValues, binding],
    )
    const { data, isPending, isFetching, isError, refetch } = useTransfers(params)
    const { exportCsv, isExporting } = useTableExport<LiveRow>({
      endpoint: '/v1/transfers/',
      columns: ctx.columns,
      columnVisibility: ctx.columnVisibility,
      params,
    })
    return {
      rows: (data?.results ?? []) as unknown as LiveRow[],
      totalCount: data?.count,
      filterFacets: [],
      isPending,
      isFetching,
      isError,
      refetch,
      exportCsv,
      isExporting,
    }
  },
  rowHref: (row) => `/transfers/${(row as { uuid: string }).uuid}`,
}

// ---------------------------------------------------------------------------
// Inspections - flat array (no server pagination); client search + CSV.
// ---------------------------------------------------------------------------

function inspectionProperty(r: InspectionRow): string {
  return (
    [r.property_address_line_1, r.property_city, r.property_state].filter(Boolean).join(', ') || '-'
  )
}

const inspectionsSource: LiveSource = {
  label: 'Inspections',
  searchPlaceholder: 'Search inspections...',
  paginated: true,
  requiresFlag: FLAGS.INSPECTIONS,
  buildColumns: (formatDate) => {
    const cols: DataTableColumn<InspectionRow>[] = [
      {
        id: 'code',
        header: 'Inspection #',
        cell: (r) => (
          <span className="font-medium text-[var(--green)]">{r.code || r.uuid.slice(0, 8).toUpperCase()}</span>
        ),
      },
      { id: 'type', header: 'Type', cell: (r) => r.sub_type_label || '-' },
      { id: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.lifecycle_status} /> },
      { id: 'inspector', header: 'Inspector', cell: (r) => r.inspector_name || '-' },
      { id: 'property', header: 'Property', cell: (r) => inspectionProperty(r) },
      {
        id: 'scheduled',
        header: 'Scheduled',
        cell: (r) => (r.scheduled_for ? formatDate(r.scheduled_for) : '-'),
      },
      { id: 'result', header: 'Result', cell: (r) => r.result || '-' },
    ]
    return cols as unknown as DataTableColumn<LiveRow>[]
  },
  useData: (ctx) => {
    const { binding } = ctx
    const extra: Record<string, string> = {}
    if (binding.status) extra.status = binding.status
    if (binding.bucket) extra.bucket = binding.bucket
    const params = useMemo(
      () => pagedParams(ctx, extra),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ctx.page, ctx.limit, ctx.search, ctx.activeFilters, ctx.filterValues, binding],
    )
    // The platform's standard page: a card counting thousands lists them all.
    const { data, isPending, isFetching, isError, refetch } = useQuery({
      queryKey: ['inspections', 'page', params],
      queryFn: async () =>
        (await api.get<{ count: number; results: InspectionRow[] }>('/inspections/', { params })).data,
    })
    const rows = (data?.results ?? []) as unknown as LiveRow[]
    // Every page, not just the one on screen.
    const exportCsv = async () => {
      const all: InspectionRow[] = []
      for (let page = 1; ; page += 1) {
        const res = await api.get<{ count: number; results: InspectionRow[] }>('/inspections/', {
          params: { ...params, page: String(page), page_size: '100' },
        })
        all.push(...res.data.results)
        if (!res.data.results.length || all.length >= res.data.count) break
      }
      clientCsv(`inspections`, ctx.columns, all as unknown as LiveRow[], (row, col) => {
        const r = row as unknown as InspectionRow
        switch (col.id) {
          case 'code':
            return r.code || r.uuid.slice(0, 8)
          case 'type':
            return r.sub_type_label || ''
          case 'status':
            return r.lifecycle_status || ''
          case 'inspector':
            return r.inspector_name || ''
          case 'property':
            return inspectionProperty(r)
          case 'scheduled':
            return r.scheduled_for || ''
          case 'result':
            return r.result || ''
          default:
            return ''
        }
      })
    }
    return {
      rows,
      totalCount: data?.count,
      filterFacets: [],
      isPending,
      isFetching,
      isError,
      refetch,
      exportCsv,
      isExporting: false,
    }
  },
  rowHref: (row) => `/inspections/${(row as unknown as InspectionRow).uuid}`,
}

// ---------------------------------------------------------------------------
// Warranties
// ---------------------------------------------------------------------------

function warrantyOwner(r: WarrantyListRow): string {
  return [r.consumer_account?.first_name, r.consumer_account?.last_name].filter(Boolean).join(' ') || '-'
}

/** One warranty column from a card's column spec (WPS-232 #10). */
function buildWarrantyColumn(
  spec: NonNullable<DataSetRef['columns']>[number],
  fmt: (iso?: string | null) => string,
): DataTableColumn<WarrantyListRow> | null {
  const label = spec.label
  const reg = (r: WarrantyListRow) => r.registration
  if (spec.id === 'partnership_attr' && spec.code_name) {
    const code = spec.code_name
    return {
      id: `partnership_attr_${code}`,
      header: label ?? prettify(code),
      cell: (r) => prettify(reg(r)?.partnership_attributes?.[code]),
    }
  }
  switch (spec.id) {
    case 'registration_number':
      return {
        id: 'registration_number',
        header: label ?? 'Registration #',
        cell: (r) => (
          <span className="font-medium text-[var(--green)]">{reg(r)?.registration_number || '-'}</span>
        ),
      }
    case 'warranty_class':
      return { id: 'warranty_class', header: label ?? 'Warranty category', cell: (r) => r.class_name || '-' }
    case 'company':
      return { id: 'company', header: label ?? 'Company', cell: (r) => reg(r)?.company || '-' }
    case 'registration_date':
      return { id: 'registration_date', header: label ?? 'Registration date', cell: (r) => fmt(reg(r)?.created_at) }
    case 'status':
      return { id: 'status', header: label ?? 'Status', cell: (r) => <StatusBadge status={r.status ?? '-'} /> }
    case 'owner':
      return { id: 'owner', header: label ?? 'Owner', cell: (r) => warrantyOwner(r) }
    case 'address':
      return { id: 'address', header: label ?? 'Address', cell: (r) => reg(r)?.address?.line_1 || '-' }
    case 'city':
      return { id: 'city', header: label ?? 'City', cell: (r) => reg(r)?.address?.city || '-' }
    case 'state':
      return { id: 'state', header: label ?? 'State', cell: (r) => reg(r)?.address?.state || '-' }
    case 'payment':
      return {
        id: 'payment',
        header: label ?? 'Payment',
        // The registration's payment workflow, else the warranty's own status.
        cell: (r) => reg(r)?.payment?.label || prettify(r.payment_status),
      }
    default:
      return null
  }
}

const warrantiesSource: LiveSource = {
  label: 'Warranties',
  searchPlaceholder: 'Search warranties...',
  paginated: true,
  requiresFlag: FLAGS.WARRANTIES,
  buildColumns: (formatDate, binding) => {
    if (binding.columns?.length) {
      const fmt = (iso?: string | null) => (iso ? formatDate(iso) : '-')
      return binding.columns
        .map((spec) => buildWarrantyColumn(spec, fmt))
        .filter((c): c is DataTableColumn<WarrantyListRow> => c !== null) as unknown as DataTableColumn<LiveRow>[]
    }
    const cols: DataTableColumn<WarrantyListRow>[] = [
      { id: 'owner', header: 'Customer', cell: (r) => warrantyOwner(r) },
      { id: 'template', header: 'Warranty', cell: (r) => r.warranty_template?.name || '-' },
      { id: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status ?? '-'} /> },
      {
        id: 'payment',
        header: 'Payment',
        cell: (r) => (r.payment_status ? <StatusBadge status={r.payment_status} /> : '-'),
      },
      { id: 'active_from', header: 'Active from', cell: (r) => (r.active_from ? formatDate(r.active_from) : '-') },
      {
        id: 'expires_at',
        header: 'Expires',
        sortable: true,
        cell: (r) => (r.expires_at ? formatDate(r.expires_at) : '-'),
      },
    ]
    return cols as unknown as DataTableColumn<LiveRow>[]
  },
  useData: (ctx) => {
    const { binding } = ctx
    const extra: Record<string, string> = {}
    if (binding.warrantyType) extra.warranty_type = binding.warrantyType
    if (binding.paymentStatus) extra.payment_status = binding.paymentStatus
    if (binding.purchasedWithinDays) extra.purchased_within_days = String(binding.purchasedWithinDays)
    // A card that names its own columns reads each row's registration too.
    if (binding.columns?.length) extra.include = 'registration'
    const params = useMemo(
      () => pagedParams(ctx, extra),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ctx.page, ctx.limit, ctx.search, ctx.activeFilters, ctx.filterValues, binding],
    )
    const { data, isPending, isFetching, isError, refetch } = useWarranties(params, { keepPreviousPage: true })
    const { exportCsv, isExporting } = useTableExport<LiveRow>({
      endpoint: '/warranties/',
      columns: ctx.columns,
      columnVisibility: ctx.columnVisibility,
      params,
    })
    return {
      rows: (data?.results ?? []) as unknown as LiveRow[],
      totalCount: data?.count,
      filterFacets: (data?.filter_options as ProductFilterFacet[] | undefined) ?? [],
      isPending,
      isFetching,
      isError,
      refetch,
      exportCsv,
      isExporting,
    }
  },
  rowHref: (row) => {
    const r = row as unknown as WarrantyListRow
    return r.holding_uuid ? `/registrations/${r.holding_uuid}` : undefined
  },
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

export const LIVE_SOURCES: Record<string, LiveSource> = {
  claims: claimsSource,
  registrations: registrationsSource,
  transfers: transfersSource,
  inspections: inspectionsSource,
  warranties: warrantiesSource,
}

/** Look up a source descriptor by its open key, or `undefined` if unregistered. */
export function getLiveSource(source: string | undefined): LiveSource | undefined {
  return source ? LIVE_SOURCES[source] : undefined
}
